import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import api, { jobDescriptionAPI } from '../services/api';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Alert from '../components/ui/Alert';
import Spinner from '../components/ui/Spinner';
import SignPasswordModal from '../components/SignPasswordModal';
import SignatureSetupModal from '../components/SignatureSetupModal';
import Modal from '../components/ui/Modal';
import { getNextPendingStep } from '../utils/courseFlow';
import { useToast } from '../context/ToastContext';

/**
 * SignaturePage — pagina de semnare a fișei SSM/SU după promovarea testului
 *
 * Flux:
 * 1. Angajatul vede fișa sa (training-ul curent)
 * 2. Dacă nu are semnătură configurată → redirect la setup
 * 3. Dacă are semnătură → apasă butonul "Semnează Fișa"
 * 4. Backend generează PDF + notifică SSM_INSTRUCTOR/COMPANY_ADMIN
 * 5. Pagina se actualizează cu statusul semnăturilor
 */
const SignaturePage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromDetails = location.state?.from === 'sheets';
  // Hint din TestPage (după trece testul): scrollTo='test' → ducem userul direct la cardul de
  // semnătură pe test, fără să mai fie nevoie să deruleze prin fișă. Ref folosit ca să facem
  // scroll DOAR la primul load (după ce datele s-au încărcat) — nu pe re-render-uri ulterioare.
  const scrollToHint = location.state?.scrollTo;
  const [scrollHintApplied, setScrollHintApplied] = useState(false);

  const [training, setTraining] = useState(null);
  const [mySignature, setMySignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [hasDirectorGeneral, setHasDirectorGeneral] = useState(false);
  const [nextCourseLink, setNextCourseLink] = useState(null);
  const [nextCourseLabel, setNextCourseLabel] = useState('');
  // Ref-uri ca să garantăm valorile corecte în Modal la momentul afișării — evită race
  // condition între setState (async) și setShowCongratsModal când recomputeNext rulează imediat
  // înainte.
  const nextCourseLinkRef = useRef(null);
  const nextCourseLabelRef = useRef('');
  // Test signatures
  const [testSignatures, setTestSignatures] = useState([]);
  const [signingTest, setSigningTest] = useState(false);
  const [testSigned, setTestSigned] = useState(false);
  const [admitting, setAdmitting] = useState(null); // 'SSM' | 'SU' | null
  // AdES — modale de confirmare parolă pentru semnătura fișei și a testului
  const [showSheetPwdModal, setShowSheetPwdModal] = useState(false);
  // Slot-urile pe care userul curent le poate semna pe fișa asta (poate fi mai mult de 1 —
  // ex: admin care e și CLM al departamentului trainee-ului semnează ca MANAGER ȘI ca ADMIN).
  // Fiecare slot = un prompt de parolă + o semnătură AdES distinctă.
  const [eligibleSlots, setEligibleSlots] = useState([]);
  const [currentSlotIdx, setCurrentSlotIdx] = useState(0);
  const [showTestPwdModal, setShowTestPwdModal] = useState(false);
  const [pendingTestTrainingId, setPendingTestTrainingId] = useState(null);
  // Bug #4: ruta /setup-signature/:id nu există → în loc de navigate (care cădea tăcut pe
  // dashboard), deschidem modalul de configurare a semnăturii direct în fluxul de semnare.
  const [showSignatureSetup, setShowSignatureSetup] = useState(false);
  // Dacă userul nu are încă cheie AdES, modalul cere și parola contului ca s-o genereze
  // în același pas (la fel ca în onboarding-ul din Layout).
  const [signatureSetupNeedsAdES, setSignatureSetupNeedsAdES] = useState(false);
  // Pop-up „Felicitări" se afișează DUPĂ ce trainee-ul a terminat de semnat tot (fișa +
  // semnătura de test, când există). Înainte popup-ul era pe TestPage imediat după test,
  // și userul putea pleca spre certificate fără să semneze. Acum apare doar când ai
  // încheiat semnarea — un singur shot pe instanță, controlat manual din handlers.
  const [showCongratsModal, setShowCongratsModal] = useState(false);

  // Deschide modalul de configurare semnătură, determinând întâi dacă trebuie să ceară și
  // parola pentru activarea AdES (cheia de semnare). Best-effort: dacă verificarea eșuează,
  // deschidem modalul fără pasul AdES (acesta poate fi finalizat ulterior din onboarding).
  const openSignatureSetup = async () => {
    try {
      const res = await api.get('/signing/status');
      setSignatureSetupNeedsAdES(!res.data?.hasSigningKey);
    } catch (_) {
      setSignatureSetupNeedsAdES(false);
    }
    setShowSignatureSetup(true);
  };

  useEffect(() => {
    fetchData();
  }, [trainingId]);

  // Auto-scroll la cardul de semnătură test când userul vine din TestPage (state.scrollTo='test').
  // Se aplică o singură dată după loading=false, ca elementul #test-sign-section să fie deja
  // randat. Dacă nu există (cursul nu are test sau e IP), scroll-ul e no-op.
  useEffect(() => {
    if (loading || scrollHintApplied || scrollToHint !== 'test') return;
    const t = setTimeout(() => {
      const el = document.getElementById('test-sign-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollHintApplied(true);
    }, 250);
    return () => clearTimeout(t);
  }, [loading, scrollToHint, scrollHintApplied]);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser.id;
  const currentUserRole = currentUser.role;
  const isOwnTraining = training?.employee?.userId === currentUserId;
  const isEmployee = currentUserRole === 'EMPLOYEE';
  // Back button rutat corect: dacă ai venit din „Fișe de semnat" → înapoi acolo;
  // cel instruit (propria fișă) → cursuri; semnatarul (admin/manager/etc) → fișe de semnat.
  const goBack = () => {
    if (cameFromDetails) return navigate(-1);
    return navigate(isOwnTraining ? '/my-courses' : '/sheets-to-sign');
  };
  const backLabel = isOwnTraining ? t('signature.backToCourses') : t('signature.backToSheets');
  const employeeData = training?.employee;
  const needsRoute = isEmployee || isOwnTraining;
  const hasRoute = !!(employeeData?.homeAddress && employeeData?.workAddress);
  const routeBlocked = needsRoute && !hasRoute;

  // Calculează următorul curs din secvență (doar pentru angajat)
  const ONBOARDING_ORDER = ['IIG_SSM', 'ILM_SSM', 'IIG_SU', 'ILM_SU'];
  const TYPE_SHORT = { IIG_SSM: 'IIG SSM', ILM_SSM: 'ILM SSM', IIG_SU: 'IIG SU', ILM_SU: 'ILM SU' };
  // Tipurile aplicabile pentru fiecare rol de trainee — replică logica matricei din
  // `signatureResolver.js`. Cei pentru care matrix-ul e null (isNotCompleted) NU intră în
  // lanțul de onboarding (e.g., COMPANY_ADMIN face DOAR IIG, nu și ILM).
  const APPLICABLE_TYPES_BY_ROLE = {
    EMPLOYEE: new Set(['IIG_SSM', 'ILM_SSM', 'IIG_SU', 'ILM_SU']),
    DIRECTOR_GENERAL: new Set(['IIG_SSM', 'ILM_SSM', 'IIG_SU', 'ILM_SU']),
    COMPANY_ADMIN: new Set(['IIG_SSM', 'IIG_SU']),
  };

  useEffect(() => {
    // Calculăm `nextCourseLink` pentru ORICE trainee, indiferent de rol (anterior era restricționat
    // la EMPLOYEE, ce făcea ca admin-i / DG care își fac propria instruire să nu fie redirectionați
    // automat la următoarea în lanț).
    if (!training?.course?.type || !isOwnTraining) return;
    const currentType = training.course.type;
    const idx = ONBOARDING_ORDER.indexOf(currentType);
    // Instruirile periodice (IP) NU sunt în ONBOARDING_ORDER, dar au propriul lanț: IP_SSM → IP_SU.
    // Le tratăm separat în computeNextStep (vezi mai jos), deci nu ieșim devreme pentru ele.
    const isIPType = currentType === 'IP_SSM' || currentType === 'IP_SU';
    // NU ieșim devreme pe ultimul curs din ONBOARDING_ORDER (ILM_SU): după el urmează încă
    // Fișa postului SU. Dacă blocam aici, ILM_SU nu mai calcula deloc nextStep → pop-up-ul de
    // după semnare sărea peste Fișa postului SU și ducea direct la certificate. `computeNextStep`
    // tratează intern și cazul „nu mai e nimic" (return null), iar bucla de cursuri e mărginită
    // corect de `i < ONBOARDING_ORDER.length`.
    if (idx < 0 && !isIPType) return;

    // Filtru per rol: ILM nu se aplică pentru COMPANY_ADMIN. Sărim tipurile neaplicabile.
    const traineeRole = training?.employee?.user?.role || 'EMPLOYEE';
    const applicableSet =
      APPLICABLE_TYPES_BY_ROLE[traineeRole] || APPLICABLE_TYPES_BY_ROLE.EMPLOYEE;

    // Calculator pentru următorul pas în flow — refolosit la mount ȘI după sign (când
    // certificatul tocmai s-a creat, FP devine materializabil în getMyFlow). Acceptă
    // current type și current domain, returnează { link, label } (null dacă nimic).
    // Expus pe `window.__resolveNextStep` ca să-l putem refolosi la sign success.
    const computeNextStep = async () => {
      try {
        // --- Instruiri periodice (IP) ---
        // După flow-ul de onboarding (IIG→ILM→FP, SSM apoi SU) urmează IP-urile, cu același
        // redirect prin săgeată. Plimbăm angajatul prin TOATE IP-urile rămase (oricâte, orice tip),
        // excluzând-o pe cea curentă tocmai semnată, ca să nu rateze niciuna.
        if (isIPType) {
          // Continuitate cu FP: toate IP SSM → FP SSM → toate IP SU → FP SU. Avem nevoie și de flow-ul
          // fișei postului ca, după ultima IP dintr-un domeniu, să trimitem automat la FP-ul lui.
          const [coursesRes, jdRes] = await Promise.all([
            api.get('/courses/my/assigned').catch(() => ({ data: [] })),
            jobDescriptionAPI.getMyFlow().catch(() => ({ data: { ssm: null, su: null } })),
          ]);
          const assignments = coursesRes.data || [];
          return getNextPendingStep(assignments, jdRes.data || {}, {
            excludeCourseId: training.course.id,
          });
        }

        const currentDomain = currentType.endsWith('_SSM') ? 'SSM' : 'SU';
        const isLastCourseInDomain =
          currentDomain === 'SSM'
            ? currentType === 'ILM_SSM' ||
              (currentType === 'IIG_SSM' && traineeRole === 'COMPANY_ADMIN')
            : currentType === 'ILM_SU' ||
              (currentType === 'IIG_SU' && traineeRole === 'COMPANY_ADMIN');

        const [coursesRes, jdRes] = await Promise.all([
          api.get('/courses/my/assigned'),
          jobDescriptionAPI.getMyFlow().catch(() => ({ data: { ssm: null, su: null } })),
        ]);
        const assignments = coursesRes.data || [];
        const flow = jdRes.data || {};
        console.log('[SignaturePage:computeNextStep]', {
          currentType,
          currentDomain,
          isLastCourseInDomain,
          traineeRole,
          flowSsm: flow.ssm,
          flowSu: flow.su,
        });

        if (isLastCourseInDomain) {
          const fp = currentDomain === 'SSM' ? flow.ssm : flow.su;
          if (
            fp &&
            fp.instanceId &&
            (fp.status === 'WAITING_EMPLOYEE' || fp.status === 'WAITING_COUNTERPART')
          ) {
            return {
              link: `/job-descriptions/${fp.instanceId}/sign`,
              label: t('signature.continueToJobDescription', { domain: currentDomain }),
            };
          }
          console.log(
            '[SignaturePage:computeNextStep] FP NOT pickable for domain',
            currentDomain,
            '— status:',
            fp?.status,
            'instanceId:',
            fp?.instanceId
          );
        }

        for (let i = idx + 1; i < ONBOARDING_ORDER.length; i++) {
          const nextType = ONBOARDING_ORDER[i];
          if (!applicableSet.has(nextType)) continue;
          const nextAssignment = assignments.find((a) => a.course?.type === nextType);
          if (nextAssignment && nextAssignment.training?.status !== 'PASSED') {
            return {
              link: `/training/${nextAssignment.course.id}`,
              label: t('signature.continueTo', { type: TYPE_SHORT[nextType] || nextType }),
            };
          }
        }
        return null;
      } catch (e) {
        return null;
      }
    };
    // Expun pentru refresh după sign. Setează ATÂT state CÂT și ref-uri ca pop-up-ul
    // să poată citi valoarea ultimă instant (state e async, ref e sync).
    window.__signaturePageRecomputeNext = async () => {
      const next = await computeNextStep();
      const link = next?.link || null;
      const label = next?.label || '';
      nextCourseLinkRef.current = link;
      nextCourseLabelRef.current = label;
      setNextCourseLink(link);
      setNextCourseLabel(label);
      return next;
    };
    (async () => {
      const next = await computeNextStep();
      const link = next?.link || null;
      const label = next?.label || '';
      nextCourseLinkRef.current = link;
      nextCourseLabelRef.current = label;
      setNextCourseLink(link);
      setNextCourseLabel(label);
    })();
  }, [training, isOwnTraining]);

  // Nu mai facem redirect automat — arătăm mesaj pe ecran

  const fetchData = async () => {
    try {
      const [trainingRes, sigRes] = await Promise.all([
        api.get(`/trainings/${trainingId}`),
        api.get('/signatures/my').catch(() => ({ data: null })),
      ]);
      setTraining(trainingRes.data);
      setMySignature(sigRes.data);

      // Verifică dacă utilizatorul CURENT a semnat deja fișa
      const alreadySigned = trainingRes.data?.signatures?.some((s) => s.signerId === currentUserId);
      if (alreadySigned) setSigned(true);

      // Fetch semnăturile de test
      try {
        const testSigRes = await api.get(`/signatures/test/${trainingId}`);
        setTestSignatures(testSigRes.data || []);
        const alreadySignedTest = (testSigRes.data || []).some(
          (s) => s.signerId === currentUserId || s.signer?.id === currentUserId
        );
        if (alreadySignedTest) setTestSigned(true);
      } catch {
        setTestSignatures([]);
      }

      // Verifică dacă firma are Director General (necesar pentru matricea de semnături)
      try {
        const companyId = trainingRes.data?.employee?.companyId;
        if (companyId) {
          const empRes = await api.get(`/employees?companyId=${companyId}`);
          const hasDG = (empRes.data || []).some((e) => e.user?.role === 'DIRECTOR_GENERAL');
          setHasDirectorGeneral(hasDG);
        }
      } catch (e) {
        /* ignore — default false */
      }
    } catch (err) {
      console.error(err);
      // Angajații — înapoi la cursuri; admins — înapoi la fișe
      navigate(
        ['EMPLOYEE', 'DIRECTOR_GENERAL'].includes(currentUserRole)
          ? '/my-courses'
          : '/sheets-to-sign'
      );
    } finally {
      setLoading(false);
    }
  };

  // Folosim noul endpoint full-sheet care concatenează fișa principală + fișele postului
  // semnate (chiar și doar de angajat). Fallback la endpoint-ul vechi dacă lipsește employee.id.
  const handleDownload = async () => {
    setPdfLoading(true);
    try {
      const empId = training?.employee?.id;
      const url = empId
        ? `/job-descriptions/employee/${empId}/full-sheet`
        : `/certificates/${trainingId}/download`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      // Nume standard de fișier (fără diacritice/id criptic): „Fisa-instruire-Nume-Prenume.pdf".
      const empName =
        `${training?.employee?.user?.firstName || ''} ${training?.employee?.user?.lastName || ''}`.trim();
      const safeName = `Fisa-instruire-${empName}`
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      link.download = `${safeName || 'Fisa-instruire'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 3000);
    } catch (err) {
      setError(t('signature.errorDownloadPdf'));
    } finally {
      setPdfLoading(false);
    }
  };

  const handleView = async () => {
    setPdfLoading(true);
    try {
      const empId = training?.employee?.id;
      const url = empId
        ? `/job-descriptions/employee/${empId}/full-sheet`
        : `/certificates/${trainingId}/download?inline=true`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      setError(t('signature.errorOpenPdf'));
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSign = async () => {
    if (!mySignature?.hasConfiguredSignature) {
      openSignatureSetup();
      return;
    }
    setError('');
    // Fetch slot-urile pe care le pot semna. De obicei e 1 (un singur slot), dar pentru
    // admin care e și CLM al departamentului trainee-ului apar 2 — ambele cer parolă separat.
    try {
      const res = await api.get(`/signatures/${trainingId}/eligible-slots`);
      const slots = res.data?.slots || [];
      if (slots.length === 0) {
        setError(t('signature.noSlotsAvailable'));
        return;
      }
      setEligibleSlots(slots);
      setCurrentSlotIdx(0);
      setShowSheetPwdModal(true);
    } catch (err) {
      setError(err.response?.data?.error || t('signature.errorCheckingSlots'));
    }
  };

  // Apelat de SignPasswordModal după ce userul a introdus parola.
  // Throw → modalul rămâne deschis (părintele afișează eroarea). Returnare ok → trecem la
  // următorul slot sau finalizăm dacă era ultimul.
  const submitSheetSignature = async (password) => {
    setSigning(true);
    const slot = eligibleSlots[currentSlotIdx];
    try {
      await api.post('/signatures', {
        trainingId,
        password,
        signAsRole: slot?.signerRole,
      });
      // Dacă mai sunt slot-uri de semnat, treci la următorul (modalul se redeschide cu parola goală).
      const nextIdx = currentSlotIdx + 1;
      if (nextIdx < eligibleSlots.length) {
        setCurrentSlotIdx(nextIdx);
        // Truc: închidem și redeschidem modalul ca să resetăm input-ul de parolă (state intern).
        setShowSheetPwdModal(false);
        setTimeout(() => setShowSheetPwdModal(true), 50);
      } else {
        // Ultimul slot — finalizăm
        setSigned(true);
        setShowSheetPwdModal(false);
        setEligibleSlots([]);
        setCurrentSlotIdx(0);
        await fetchData();
        // Re-compute nextStep — certificatul tocmai s-a creat, fișa postului devine materializabilă
        // în getMyFlow. Fără asta, pop-up-ul afișa fallback (IIG_SU) în loc de Fișa postului SU.
        if (typeof window.__signaturePageRecomputeNext === 'function') {
          await window.__signaturePageRecomputeNext();
        }
        // Trainee-ul (cel instruit) — verificăm dacă a terminat TOTUL (fișa + semnătura de
        // test, dacă cursul are test). Dacă da, popup-ul de felicitări cu buton „Continuă la <next>"
        // (click manual). Altfel scrollăm la secțiunea de semnat testul.
        const hasFinalTest = (training?.course?.finalTest?.questions?.length || 0) > 0;
        if (isOwnTraining && (!hasFinalTest || testSigned)) {
          setShowCongratsModal(true);
        } else {
          setTimeout(() => {
            document
              .getElementById('test-sign-section')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        }
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresSignatureSetup) {
        setShowSheetPwdModal(false);
        openSignatureSetup();
        return;
      }
      if (data?.requiresRoute) {
        setShowSheetPwdModal(false);
        navigate('/my-route');
        return;
      }
      if (data?.requiresAdESActivation) {
        setShowSheetPwdModal(false);
        setError(data.error || t('signature.activateAdESFull'));
        return;
      }
      throw err; // modalul afișează mesajul
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return <Spinner label={t('signature.loadingSheet')} />;
  }

  if (!training) return null;

  // Ecran de atenționare dacă traseul nu e configurat
  if (routeBlocked && (isEmployee || isOwnTraining)) {
    return (
      <div className="max-w-lg mx-auto mt-12">
        <Card className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-warning-100 dark:bg-warning-950/50 flex items-center justify-center mx-auto mb-4 text-warning-600 dark:text-warning-300">
            <svg
              className="w-9 h-9"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-warning-900 dark:text-warning-300 mb-2">
            {t('signature.routeIncompleteTitle')}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mb-6 text-sm">
            {t('signature.routeIncompleteDesc')}
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={() =>
                navigate('/my-route', { state: { returnTo: `/training/${trainingId}/sign` } })
              }
            >
              {t('signature.completeRoute')}
            </Button>
            <Button variant="ghost" size="sm" onClick={goBack}>
              {backLabel}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const signatures = training.signatures || [];
  const employee = training.employee;
  const course = training.course;

  // Definește semnatarii necesari în funcție de tipul cursului și rolul angajatului
  const isSU = course?.type?.endsWith('_SU');
  const courseType = course?.type;
  const traineeRole = training.employee?.user?.role || 'EMPLOYEE';

  // Mapare tip semnatar abstract → UI (roluri, label, icon)
  const signerTypeToUI = (type) => {
    switch (type) {
      case 'EMPLOYEE':
        return {
          role: ['EMPLOYEE', 'DIRECTOR_GENERAL', 'COMPANY_ADMIN'],
          label: t('signature.signerEmployee'),
          signerType: 'EMPLOYEE',
        };
      case 'SSM':
        return {
          role: ['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'],
          label: t('signature.signerSSM'),
          signerType: 'SSM',
        };
      case 'CADRU_PSI':
        return {
          role: ['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'],
          label: t('signature.signerCadruPSI'),
          signerType: 'CADRU_PSI',
        };
      case 'MANAGER':
        return {
          role: null,
          label: t('signature.signerManager'),
          signerType: 'MANAGER',
        };
      case 'DIRECTOR':
        return {
          role: ['DIRECTOR_GENERAL'],
          label: t('signature.signerDirector'),
          signerType: 'DIRECTOR',
        };
      case 'ADMIN':
        return {
          role: ['COMPANY_ADMIN'],
          label: t('signature.signerAdmin'),
          signerType: 'ADMIN',
        };
      default:
        return { role: [type], label: type, signerType: type };
    }
  };

  // CLM trainee = backend ne spune; fallback pe department.managerId
  const isTraineeCLM =
    !!training._isTraineeCLM ||
    training.employee?.department?.managerId === training.employee?.userId;

  // Matricea de semnături — reproduce logica din backend signatureResolver
  const getRequiredSignerTypes = (type, role, _hasDG) => {
    const isDG = role === 'DIRECTOR_GENERAL';
    const isAdmin = role === 'COMPANY_ADMIN';

    // COMPANY_ADMIN trainee — reguli speciale
    if (isAdmin) {
      switch (type) {
        case 'IIG_SSM':
          return ['EMPLOYEE', 'SSM', 'SSM'];
        case 'ILM_SSM':
          return null; // NOT_COMPLETED
        case 'IP_SSM':
          return ['EMPLOYEE', 'SSM', 'SSM'];
        case 'SUPLIMENTARE_SSM':
          return ['EMPLOYEE', 'SSM', 'SSM'];
        case 'IIG_SU':
          return ['EMPLOYEE', 'CADRU_PSI'];
        case 'ILM_SU':
          return null; // NOT_COMPLETED
        // SU = domeniul PSI → verificator Cadru Tehnic PSI, nu SSM (sincron cu backend).
        case 'IP_SU':
          return ['EMPLOYEE', 'CADRU_PSI'];
        case 'SUPLIMENTARE_SU':
          return null;
        default:
          return ['EMPLOYEE', 'SSM'];
      }
    }

    // DIRECTOR_GENERAL trainee SAU ANGAJAT care e el însuși CLM pe propria fișă: niciunul nu se poate
    // semna pe sine la slotul de conducător → ADMINUL preia verificarea (MEREU adminul). Aceeași matrice.
    if (isDG || isTraineeCLM) {
      switch (type) {
        case 'IIG_SSM':
          return ['EMPLOYEE', 'SSM', 'SSM'];
        case 'ILM_SSM':
        case 'IP_SSM':
        case 'SUPLIMENTARE_SSM':
          return ['EMPLOYEE', 'ADMIN', 'ADMIN'];
        case 'IIG_SU':
          return ['EMPLOYEE', 'CADRU_PSI'];
        case 'ILM_SU':
        case 'IP_SU':
          return ['EMPLOYEE', 'ADMIN'];
        case 'SUPLIMENTARE_SU':
          return null;
        default:
          return ['EMPLOYEE', 'ADMIN'];
      }
    }

    // ANGAJAT normal (nu e el însuși CLM) — slotul de conducător e semnat de CLM-ul lui (MANAGER).
    switch (type) {
      case 'IIG_SSM':
        return ['EMPLOYEE', 'SSM', 'SSM'];
      case 'ILM_SSM':
      case 'IP_SSM':
      case 'SUPLIMENTARE_SSM':
        return ['EMPLOYEE', 'MANAGER', 'ADMIN'];
      case 'IIG_SU':
        return ['EMPLOYEE', 'CADRU_PSI'];
      case 'ILM_SU':
      case 'IP_SU':
        return ['EMPLOYEE', 'MANAGER'];
      case 'SUPLIMENTARE_SU':
        return null;
      default:
        return ['EMPLOYEE', 'SSM'];
    }
  };

  const signerTypes = getRequiredSignerTypes(courseType, traineeRole, hasDirectorGeneral);
  const isNotCompleted = signerTypes === null;

  // Convertește tipurile abstracte în obiecte UI cu order
  // Admis la lucru NU apare aici — se semnează separat prin endpoint dedicat
  const requiredSigners = isNotCompleted
    ? []
    : signerTypes.map((type, idx) => ({
        ...signerTypeToUI(type),
        order: idx + 1,
      }));

  const signerIcons = [
    // EMPLOYEE
    <svg
      key="employee"
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
      />
    </svg>,
    // INSTRUCTOR
    <svg
      key="instructor"
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>,
    // COMPANY_ADMIN
    <svg
      key="company-admin"
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
      />
    </svg>,
  ];

  // Găsește semnătura pentru un slot, cu fallback legacy:
  // semnăturile vechi de manager erau stocate cu signerRole='EMPLOYEE'.
  // Pentru slot MANAGER, acceptăm și o semnătură EMPLOYEE care NU e a celui instruit.
  const getSignatureForSlot = (rs) => {
    const rolesArr = Array.isArray(rs.role) ? rs.role : rs.role ? [rs.role] : [];
    if (rs.signerType === 'EMPLOYEE') {
      return signatures.find((s) => s.signerRole === 'EMPLOYEE' && s.signerId === employee?.userId);
    }
    if (rs.signerType === 'MANAGER') {
      return signatures.find(
        (s) =>
          s.signerRole === 'MANAGER' ||
          (s.signerRole === 'EMPLOYEE' && s.signerId !== employee?.userId)
      );
    }
    if (rs.signerType === 'SSM') {
      return signatures.find(
        (s) =>
          ['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'].includes(s.signerRole) ||
          s.signer?.isSSMInstructor
      );
    }
    if (rs.signerType === 'CADRU_PSI') {
      return signatures.find((s) => s.signer?.isCadruTehnicPSI);
    }
    if (rs.signerType === 'DIRECTOR') {
      return signatures.find((s) => s.signerRole === 'DIRECTOR_GENERAL');
    }
    if (rs.signerType === 'ADMIN') {
      return signatures.find((s) => s.signerRole === 'COMPANY_ADMIN');
    }
    return signatures.find((s) => rolesArr.includes(s.signerRole));
  };

  const getSignatureForRole = (roles) => {
    // Compat helper pentru codul vechi care primește direct rs.role
    const rolesArr = Array.isArray(roles) ? roles : [roles];
    return signatures.find((s) => rolesArr.includes(s.signerRole));
  };

  // A semnat utilizatorul curent (nu angajatul)?
  const currentUserHasSigned = signatures.some((s) => s.signerId === currentUserId);
  const employeeHasSigned = getSignatureForRole('EMPLOYEE') !== undefined;
  const allSigned =
    isNotCompleted || requiredSigners.every((rs) => getSignatureForSlot(rs) !== undefined);
  const totalSigned = requiredSigners.filter((rs) => getSignatureForSlot(rs) !== undefined).length;

  // Slotul utilizatorului curent — match strict pe tipul de semnatar
  const isOwnTrainingForUI = currentUserId === employee?.userId;
  const isDeptManagerOfTrainee = !!training.canSignAsManager;
  const matchesSignerSlot = (rs) => {
    if (rs.signerType === 'EMPLOYEE') return isOwnTrainingForUI;
    if (rs.signerType === 'MANAGER') return isDeptManagerOfTrainee;
    if (rs.signerType === 'SSM') {
      return (
        ['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'].includes(currentUserRole) ||
        currentUser.isSSMInstructor
      );
    }
    if (rs.signerType === 'CADRU_PSI') return !!currentUser.isCadruTehnicPSI;
    // ADMIN: COMPANY_ADMIN real SAU persoană împuternicită (AdminMandate activ — flag de la backend).
    if (rs.signerType === 'ADMIN')
      return currentUserRole === 'COMPANY_ADMIN' || !!training.canSignAsAdmin;
    if (rs.signerType === 'DIRECTOR') return currentUserRole === 'DIRECTOR_GENERAL';
    return false;
  };

  const mySlotIdx = requiredSigners.findIndex((rs) => matchesSignerSlot(rs));

  // Pentru IIG/ILM SSM/SU, oricine are de semnat testul TREBUIE să o facă înainte de a părăsi
  // pagina. Folosit ca să blocăm:
  //   (a) butonul „Continuă la <next curs>" (trainee → următoarea instruire);
  //   (b) butoanele „Vezi Fișa (versiune curentă)" și „Înapoi la cursuri/fișe" (toți semnatarii).
  // Nu vrem ca nimeni să aibă tendința să iasă de pe pagină fără să-și fi pus semnătura pe test
  // după ce și-a pus-o pe fișă — testul e o semnătură separată ușor de uitat.
  // IP-urile rămân lejere (cerință legală) — nu blocăm acolo.
  const isStrictTestCourse = ['IIG_SSM', 'ILM_SSM', 'IIG_SU', 'ILM_SU'].includes(
    training?.course?.type
  );
  const hasFinalTestForBlock = (training?.course?.finalTest?.questions?.length || 0) > 0;
  // Userul curent are slot de semnătură pe test pentru această instruire?
  //   - trainee: mereu (își semnează propriul test);
  //   - IIG_SSM: rol SSM / flag isSSMInstructor;
  //   - IIG_SU: flag isCadruTehnicPSI;
  //   - ILM_*: dept manager al trainee-ului (CLM) SAU admin firmă când trainee-ul e CLM.
  const courseTypeForTestSig = training?.course?.type;
  const isIIGForTest = courseTypeForTestSig?.startsWith('IIG_');
  const isSSMTypeForTest = courseTypeForTestSig?.endsWith('_SSM');
  const isTraineeCLMForTest = isTraineeCLM; // backend trimite `_isTraineeCLM` (vezi linia de mai sus)
  let currentUserSignsThisTest = false;
  if (isOwnTrainingForUI) currentUserSignsThisTest = true;
  else if (isIIGForTest && isSSMTypeForTest)
    currentUserSignsThisTest =
      ['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'].includes(currentUserRole) ||
      !!currentUser.isSSMInstructor;
  else if (isIIGForTest && !isSSMTypeForTest)
    currentUserSignsThisTest = !!currentUser.isCadruTehnicPSI;
  else if (!isIIGForTest)
    currentUserSignsThisTest =
      isDeptManagerOfTrainee || (currentUserRole === 'COMPANY_ADMIN' && isTraineeCLMForTest);
  // Blocaj general — orice semnatar care nu și-a pus semnătura pe test încă.
  const currentUserMustSignTestFirst =
    isStrictTestCourse && hasFinalTestForBlock && currentUserSignsThisTest && !testSigned;
  // Blocaj specific pentru trainee (folosit pentru butonul „Continuă la <next curs>" — doar
  // trainee-ul are buton de continuare). Subset al variabilei generale.
  const traineeMustSignTestFirst = isOwnTrainingForUI && currentUserMustSignTestFirst;

  // #14: „trainee simplu" = cel instruit care își vede propria fișă și NU are niciun slot de
  // semnătură în afară de al lui (nu e și admin/CLM/SSM/PSI pe această fișă). Pentru el ascundem
  // statusul celorlalți semnatari (admin/CLM/SSM) — nu-l interesează cine mai are de semnat.
  const hasNonEmployeeSlot = requiredSigners.some(
    (rs) => rs.signerType !== 'EMPLOYEE' && matchesSignerSlot(rs)
  );
  const isPlainTrainee = isOwnTrainingForUI && !hasNonEmployeeSlot;

  const prevSlotFilled = mySlotIdx <= 0 || !!getSignatureForSlot(requiredSigners[mySlotIdx - 1]);

  // Pentru sloturi non-EMPLOYEE: doar persoana care a atribuit cursul (sau MANAGER/ADMIN locali) poate semna
  const isOwnTrainingForSign = currentUserId === employee?.userId;
  const assignedBy = training.assignment?.assignedBy;
  const mySlotType = requiredSigners[mySlotIdx]?.signerType;
  const requiresAssigner = ['SSM', 'CADRU_PSI'].includes(mySlotType);
  const assignerCheckOk = !requiresAssigner || isOwnTrainingForSign || assignedBy === currentUserId;

  const canSignNow =
    !isNotCompleted &&
    mySlotIdx >= 0 &&
    !currentUserHasSigned &&
    prevSlotFilled &&
    !routeBlocked &&
    assignerCheckOk;

  // Admis la lucru — vizibil doar pentru COMPANY_ADMIN, per angajat
  const isAdmin = currentUserRole === 'COMPANY_ADMIN';
  const admittedSSM = !!employee?.admittedSSMBy;
  const admittedSU = !!employee?.admittedSUBy;
  // Verifică dacă IIG+ILM sunt complet semnate (din toate training-urile angajatului)
  // Simplificat: dacă suntem pe orice training al angajatului, verificăm din date disponibile
  // TODO: backend-ul verifică oricum, aici doar arătăm butonul dacă e admin
  // Admis SSM apare doar pe training-uri SSM, Admis SU doar pe SU
  // Admiterea la lucru se face acum exclusiv din pagina „Fișe de Semnat" (cu modal de confirmare)
  const showAdmisSSM = false;
  const showAdmisSU = false;

  const handleAdmit = async (domain) => {
    setAdmitting(domain);
    try {
      await api.post('/signatures/admit', { employeeId: employee?.id, domain });
      await fetchData();
      setAdmitting(null);
    } catch (err) {
      setAdmitting(null);
      const msg = err.response?.data?.error || t('signature.errorAdmit');
      setError(msg);
    }
  };

  // Submit pentru semnătura de test — folosit de SignPasswordModal-ul testului
  const submitTestSignature = async (password) => {
    setSigningTest(true);
    try {
      await api.post('/signatures/test', { trainingId: pendingTestTrainingId, password });
      setTestSigned(true);
      setShowTestPwdModal(false);
      const res = await api.get(`/signatures/test/${pendingTestTrainingId}`);
      setTestSignatures(res.data || []);
      fetchData();
      // Trainee a terminat de semnat fișa ȘI testul → re-compute next + popup felicitări
      // (asigură că dacă fișa postului a devenit materializabilă, butonul „Continuă la FP" apare).
      if (isOwnTraining && signed) {
        if (typeof window.__signaturePageRecomputeNext === 'function') {
          await window.__signaturePageRecomputeNext();
        }
        setShowCongratsModal(true);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresAdESActivation) {
        setShowTestPwdModal(false);
        showToast(data.error || t('signature.activateAdESProfile'), 'error');
        return;
      }
      throw err;
    } finally {
      setSigningTest(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {showSheetPwdModal &&
        (() => {
          const slot = eligibleSlots[currentSlotIdx];
          const isMulti = eligibleSlots.length > 1;
          const stepLabel = isMulti
            ? t('signature.stepLabel', {
                current: currentSlotIdx + 1,
                total: eligibleSlots.length,
              })
            : '';
          const title = slot
            ? t('signature.signAsTitle', { label: slot.label, step: stepLabel })
            : undefined;
          const description =
            slot && isMulti
              ? t('signature.signMultiDesc', {
                  label: slot.label,
                  course: course?.title || '',
                })
              : t('signature.confirmSheetDesc', { course: course?.title || '' });
          return (
            <SignPasswordModal
              title={title}
              description={description}
              onSubmit={submitSheetSignature}
              onClose={() => {
                setShowSheetPwdModal(false);
                setEligibleSlots([]);
                setCurrentSlotIdx(0);
              }}
            />
          );
        })()}
      {showTestPwdModal && (
        <SignPasswordModal
          description={t('signature.confirmTestDesc', { course: course?.title || '' })}
          onSubmit={submitTestSignature}
          onClose={() => setShowTestPwdModal(false)}
        />
      )}
      {/* Bug #4: configurarea semnăturii se face inline în fluxul de semnare (ruta dedicată
          /setup-signature/:id nu există). După succes, reîncărcăm datele ca butonul de semnare
          să devină disponibil imediat. */}
      {showSignatureSetup && (
        <SignatureSetupModal
          isFirstTime={true}
          requireAdESPassword={signatureSetupNeedsAdES}
          onClose={() => setShowSignatureSetup(false)}
          onLater={() => setShowSignatureSetup(false)}
          onSuccess={async () => {
            setShowSignatureSetup(false);
            setSignatureSetupNeedsAdES(false);
            await fetchData();
          }}
        />
      )}
      {/* Header */}
      <Card>
        <h1 className="page-title flex items-center gap-2">
          <svg
            className="w-6 h-6 text-primary-600 dark:text-primary-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
            />
          </svg>
          {t('signature.signButton')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {isSU ? t('signature.sheetIndividualSU') : t('signature.sheetIndividualSSM')}
          {' — '}
          <span className="font-medium text-slate-700 dark:text-slate-300">{course?.title}</span>
        </p>
      </Card>

      {/* Status — contextual */}
      <div
        className={`${training.passed ? 'bg-accent-50 dark:bg-accent-950/50 border-accent-200 dark:border-accent-700' : 'bg-primary-50 dark:bg-primary-950/50 border-primary-200 dark:border-primary-700'} border-2 rounded-xl p-5 flex items-center gap-4`}
      >
        <div
          className={`w-12 h-12 rounded-xl ${training.passed ? 'bg-accent-100 dark:bg-accent-900/50 text-accent-600 dark:text-accent-300' : 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-300'} flex items-center justify-center flex-shrink-0`}
        >
          <svg
            className="w-7 h-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z"
            />
          </svg>
        </div>
        <div>
          <p
            className={`font-bold ${training.passed ? 'text-accent-900 dark:text-accent-200' : 'text-primary-900 dark:text-primary-200'} text-lg`}
          >
            {training.passed
              ? training.score != null
                ? t('signature.testPassedWithScore', { score: training.score })
                : t('signature.trainingCompleted')
              : t('signature.materialsCompletedConfirm')}
          </p>
          <p
            className={`${training.passed ? 'text-accent-700 dark:text-accent-300' : 'text-primary-700 dark:text-primary-300'} text-sm mt-0.5`}
          >
            {t('signature.completedAt')}{' '}
            {training.completedAt
              ? new Date(training.completedAt).toLocaleDateString('ro-RO', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })
              : '—'}
          </p>
        </div>
        {training.certificate && (
          <div className="ml-auto">
            <span className="text-xs font-mono bg-accent-100 dark:bg-accent-950/50 text-accent-800 dark:text-accent-300 px-3 py-1 rounded-full border border-accent-300 dark:border-accent-700">
              {training.certificate.certificateNumber}
            </span>
          </div>
        )}
      </div>

      {/* Status semnături */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {isPlainTrainee ? t('signature.yourSheet') : t('signature.signaturesStatus')}
          </h2>
          <Badge color={(isPlainTrainee ? currentUserHasSigned : allSigned) ? 'green' : 'yellow'}>
            {isPlainTrainee
              ? currentUserHasSigned
                ? t('signature.signedByYou')
                : t('signature.toSign')
              : t('signature.signaturesCount', {
                  signed: totalSigned,
                  total: requiredSigners.length,
                })}
          </Badge>
        </div>

        {/* #14: pentru trainee-ul simplu, după ce și-a semnat fișa, îl liniștim că nu mai are nimic
            de făcut — fără să-i listăm semnatarii care urmează (admin/CLM/SSM), care nu-l interesează. */}
        {isPlainTrainee && currentUserHasSigned && (
          <p className="text-sm text-accent-700 dark:text-accent-300 mb-3">
            {t('signature.traineeDoneNote')}
          </p>
        )}

        <div className="space-y-3">
          {requiredSigners.map((rs, idx) => {
            // #14: trainee-ul simplu vede DOAR rândul propriu, nu și statusul celorlalți semnatari.
            if (isPlainTrainee && rs.signerType !== 'EMPLOYEE') return null;
            const sig = getSignatureForSlot(rs);
            const isSigned = !!sig;
            const isMyTurn =
              !isSigned && (idx === 0 || getSignatureForSlot(requiredSigners[idx - 1]));

            return (
              <div
                key={idx}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 transition ${
                  isSigned
                    ? 'border-accent-300 dark:border-accent-700 bg-accent-50 dark:bg-accent-950/50'
                    : isMyTurn
                      ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50'
                      : 'border-slate-200 dark:border-slate-800 bg-surface-secondary'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isSigned
                      ? 'bg-accent-100 dark:bg-accent-950/50 text-accent-600 dark:text-accent-300'
                      : isMyTurn
                        ? 'bg-primary-100 dark:bg-primary-950/50 text-primary-600 dark:text-primary-300'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-400'
                  }`}
                >
                  {signerIcons[idx] || signerIcons[0]}
                </div>
                <div className="flex-1">
                  <p
                    className={`font-semibold text-sm ${isSigned ? 'text-accent-800 dark:text-accent-300' : 'text-slate-700 dark:text-slate-300'}`}
                  >
                    {rs.label}
                  </p>
                  {isSigned && sig.signer && (
                    <>
                      <p className="text-xs text-accent-600 dark:text-accent-300 mt-0.5">
                        {t('signature.signedByAt', {
                          name: `${sig.signer.firstName} ${sig.signer.lastName}`,
                          date: new Date(sig.signedAt).toLocaleDateString('ro-RO'),
                        })}
                      </p>
                      {sig.adesSignature && (
                        <p className="text-[10px] text-accent-700 dark:text-accent-400 mt-0.5 inline-flex items-center gap-1 font-medium">
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            />
                          </svg>
                          AdES • eIDAS{sig.adesTimestampedAt ? ' • timestamp' : ''}
                        </p>
                      )}
                    </>
                  )}
                  {!isSigned && isMyTurn && matchesSignerSlot(rs) && (
                    <p className="text-xs text-primary-600 dark:text-primary-300 mt-0.5 flex items-center gap-1">
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                        />
                      </svg>
                      {t('signature.yourTurnToSign')}
                    </p>
                  )}
                  {!isSigned && isMyTurn && !matchesSignerSlot(rs) && (
                    <p className="text-xs text-warning-600 dark:text-warning-300 mt-0.5">
                      {t('signature.waitingSignatureNotified')}
                    </p>
                  )}
                  {!isSigned && !isMyTurn && (
                    <p className="text-xs text-slate-400 dark:text-slate-400 mt-0.5">
                      {t('signature.waitingPreviousSignatures')}
                    </p>
                  )}
                </div>
                <div>
                  {isSigned ? (
                    <div className="w-8 h-8 rounded-full bg-accent-100 dark:bg-accent-950/50 flex items-center justify-center text-accent-600 dark:text-accent-300">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                    </div>
                  ) : isMyTurn ? (
                    <span className="w-6 h-6 rounded-full bg-primary-400 animate-pulse block"></span>
                  ) : (
                    <div className="w-8 h-8 rounded-full border-2 border-slate-200 dark:border-slate-800 flex items-center justify-center text-slate-300 dark:text-slate-600">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Bloc semnare */}
      {canSignNow ? (
        <Card className="border-2 border-primary-200 dark:border-primary-700">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">
            {t('signature.title')}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            <Trans
              i18nKey="signature.signConfirmText"
              values={{
                course: course?.title || '',
                domain: isSU ? t('signature.domainSU') : t('signature.domainSSM'),
              }}
              components={{ b: <strong /> }}
            />
          </p>

          {/* Preview semnătură */}
          {mySignature?.savedSignature && (
            <div className="mb-4 p-4 bg-surface-secondary rounded-xl border border-slate-200 dark:border-slate-800">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                {t('signature.yourSavedSignature')}
              </p>
              <img
                src={mySignature.savedSignature}
                alt={t('signature.yourSignature')}
                className="h-16 object-contain"
              />
            </div>
          )}

          {error && (
            <Alert type="error" className="mb-4">
              {error}
            </Alert>
          )}

          {/* Cerințe */}
          <div className="mb-4 space-y-2">
            {[
              {
                check: mySignature?.hasConfiguredSignature,
                label: t('signature.reqSignatureConfigured'),
              },
              ...(needsRoute ? [{ check: hasRoute, label: t('signature.reqRouteCompleted') }] : []),
            ].map((req, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                {req.check ? (
                  <svg
                    className="w-4 h-4 text-accent-500 dark:text-accent-300 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4 text-danger-500 dark:text-danger-300 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                <span
                  className={
                    req.check
                      ? 'text-slate-700 dark:text-slate-300'
                      : 'text-danger-600 dark:text-danger-300'
                  }
                >
                  {req.label}
                </span>
              </div>
            ))}
          </div>

          {!mySignature?.hasConfiguredSignature ? (
            <Button variant="secondary" size="lg" className="w-full" onClick={openSignatureSetup}>
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              {t('signature.setupSignature')}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleSign}
              loading={signing}
              disabled={signing}
            >
              {signing ? (
                t('signature.signingGeneratingPdf')
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                    />
                  </svg>
                  {t('signature.signButton')}
                </>
              )}
            </Button>
          )}
        </Card>
      ) : currentUserHasSigned || signed ? (
        <Card>
          {allSigned ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-accent-100 dark:bg-accent-950/50 flex items-center justify-center mx-auto mb-3 text-accent-600 dark:text-accent-300">
                <svg
                  className="w-9 h-9"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-accent-900 dark:text-accent-200 mb-2">
                {t('signature.sheetFullySigned')}
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mb-5 text-sm">
                {t('signature.allSignaturesCollected')}
              </p>
              {/* Pentru IIG/ILM, blocăm orice cale de ieșire de pe pagină până când utilizatorul
                  își semnează testul (trainee, CLM, SSM, Cadru PSI — toți cei care au de semnat).
                  Vezi `currentUserMustSignTestFirst`. */}
              {currentUserMustSignTestFirst && (
                <div className="mb-4 p-3 rounded-lg bg-warning-50 dark:bg-warning-950/30 border border-warning-200 dark:border-warning-700 text-sm text-warning-800 dark:text-warning-200 flex items-start gap-2 text-left">
                  <svg
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                    />
                  </svg>
                  <div>
                    <p className="font-semibold">{t('signature.stillNeedSignTest')}</p>
                    <p className="text-xs mt-0.5">{t('signature.signTestBeforeLeaving')}</p>
                  </div>
                </div>
              )}
              <div className="flex gap-3 justify-center flex-wrap">
                {nextCourseLink && !traineeMustSignTestFirst && (
                  <Button variant="accent" size="lg" onClick={() => navigate(nextCourseLink)}>
                    {nextCourseLabel} →
                  </Button>
                )}
                {/* Vezi/Descarcă PDF + Înapoi — ascunse când userul curent mai are de semnat testul.
                    Vrem să nu existe nicio cale „înapoi" până când testul e semnat — userul ar putea
                    avea tendința să iasă crezând că a terminat. */}
                {training.certificate && !currentUserMustSignTestFirst && (
                  <>
                    <Button
                      variant={nextCourseLink ? 'secondary' : 'accent'}
                      onClick={handleView}
                      disabled={pdfLoading}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      {pdfLoading ? t('signature.opening') : t('signature.viewSheetPdf')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDownload}
                      disabled={pdfLoading}
                      title={t('signature.downloadToDisk')}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                        />
                      </svg>
                    </Button>
                  </>
                )}
                {!currentUserMustSignTestFirst && (
                  <Button variant="ghost" size="sm" onClick={goBack}>
                    {backLabel}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-950/50 flex items-center justify-center mx-auto mb-3 text-primary-600 dark:text-primary-300">
                <svg
                  className="w-9 h-9"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-primary-900 dark:text-primary-200 mb-2">
                {t('signature.youSignedSheet')}
              </h2>
              {(() => {
                const hasFinalTest = training.course?.finalTest?.questions?.length > 0;
                const testNotPassed = !training.passed;
                const needsTest = hasFinalTest && testNotPassed;

                return needsTest ? (
                  <>
                    <p className="text-slate-600 dark:text-slate-300 mb-4 text-sm">
                      {t('signature.confirmedGoToTest')}
                    </p>
                    <div className="flex gap-3 justify-center flex-wrap">
                      <Button
                        variant="accent"
                        size="lg"
                        onClick={() => navigate(`/training/${trainingId}/test`)}
                      >
                        {t('signature.continueToTest')} →
                      </Button>
                      <Button variant="ghost" size="sm" onClick={goBack}>
                        {backLabel}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-slate-600 dark:text-slate-300 mb-2 text-sm">
                      {isEmployee
                        ? t('signature.notifiedInstructor')
                        : t('signature.signatureAdded')}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-400 mb-5">
                      {t('signature.pdfAutoUpdate')}
                    </p>
                    {/* Blochez continuarea la următoarea instruire până când trainee semnează testul
                        (pentru IIG/ILM). Vezi `traineeMustSignTestFirst`. */}
                    {traineeMustSignTestFirst && (
                      <div className="mb-4 p-3 rounded-lg bg-warning-50 dark:bg-warning-950/30 border border-warning-200 dark:border-warning-700 text-sm text-warning-800 dark:text-warning-200 flex items-start gap-2 text-left">
                        <svg
                          className="w-5 h-5 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                          />
                        </svg>
                        <div>
                          <p className="font-semibold">{t('signature.stillNeedSignTest')}</p>
                          <p className="text-xs mt-0.5">{t('signature.signTestToContinue')}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3 justify-center flex-wrap">
                      {nextCourseLink && !traineeMustSignTestFirst && (
                        <Button variant="accent" size="lg" onClick={() => navigate(nextCourseLink)}>
                          {nextCourseLabel} →
                        </Button>
                      )}
                      {/* Vezi/Descarcă PDF + Înapoi — ascunse când userul curent mai are de semnat
                          testul. Fără ele nu există nicio cale „înapoi" până când nu pune semnătura. */}
                      {training.certificate && !currentUserMustSignTestFirst && (
                        <>
                          <Button variant="primary" onClick={handleView} disabled={pdfLoading}>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            {pdfLoading ? t('signature.opening') : t('signature.viewSheetCurrent')}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleDownload}
                            disabled={pdfLoading}
                            title={t('signature.downloadToDisk')}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                              />
                            </svg>
                          </Button>
                        </>
                      )}
                      {!currentUserMustSignTestFirst && (
                        <Button variant="ghost" size="sm" onClick={goBack}>
                          {backLabel}
                        </Button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </Card>
      ) : !employeeHasSigned && !isEmployee ? (
        <Card className="text-center py-4">
          <div className="w-16 h-16 rounded-full bg-warning-100 dark:bg-warning-950/50 flex items-center justify-center mx-auto mb-3 text-warning-600 dark:text-warning-300">
            <svg
              className="w-9 h-9"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
            {t('signature.waitEmployeeSign')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            {t('signature.employeeSignsFirst')}
          </p>
        </Card>
      ) : null}

      {/* Admis la lucru — doar pentru COMPANY_ADMIN */}
      {(showAdmisSSM || showAdmisSU) && (
        <Card>
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t('signature.admittedTitle')}</h2>
          <p className="text-sm text-gray-600 mb-4">{t('signature.admittedDesc')}</p>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <div className="space-y-3">
            {showAdmisSSM && (
              <button
                onClick={() => handleAdmit('SSM')}
                disabled={!!admitting}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {admitting === 'SSM' ? t('signature.processing') : t('signature.admitSSM')}
              </button>
            )}
            {showAdmisSU && (
              <button
                onClick={() => handleAdmit('SU')}
                disabled={!!admitting}
                className="w-full py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50 text-sm"
              >
                {admitting === 'SU' ? t('signature.processing') : t('signature.admitSU')}
              </button>
            )}
          </div>
          {admittedSSM && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-700">
              <span>✓</span> {t('signature.admittedSSMSigned')}
            </div>
          )}
          {admittedSU && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-700">
              <span>✓</span> {t('signature.admittedSUSigned')}
            </div>
          )}
        </Card>
      )}

      {/* Admis la lucru status — vizibil pentru toți */}
      {!isAdmin && (admittedSSM || admittedSU) && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t('signature.admittedTitle')}
          </h2>
          {admittedSSM && (
            <p className="text-sm text-green-700">{t('signature.admittedSSMShort')}</p>
          )}
          {admittedSU && <p className="text-sm text-green-700">{t('signature.admittedSUShort')}</p>}
        </Card>
      )}

      {/* Test rezolvat — doar dacă testul a fost promovat */}
      {training.passed && training.course?.finalTest?.questions?.length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-primary-600 dark:text-primary-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
              />
            </svg>
            {t('signature.testLabel', { course: course?.title })}
            {training.score != null && (
              <Badge color={training.score >= 80 ? 'green' : 'red'}>
                {t('signature.scoreLabel', { score: training.score })}
              </Badge>
            )}
          </h2>
          <div className="space-y-4">
            {training.course.finalTest.questions.map((q, qi) => {
              const testResult = training.testResults?.[0];
              const userAnswers = testResult?.answers || {};
              const selectedAnswerId = userAnswers[q.id];
              return (
                <div
                  key={q.id}
                  className="border border-slate-200 dark:border-slate-800 rounded-lg p-3"
                >
                  <p className="font-medium text-sm text-slate-900 dark:text-slate-100 mb-2">
                    {qi + 1}. {q.text}
                  </p>
                  <div className="space-y-1">
                    {(q.answers || []).map((a) => {
                      const isSelected = a.id === selectedAnswerId;
                      const isCorrect = a.isCorrect;
                      return (
                        <div
                          key={a.id}
                          className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${
                            isSelected && isCorrect
                              ? 'bg-accent-50 dark:bg-accent-950/50 text-accent-700 dark:text-accent-300 font-medium'
                              : isSelected && !isCorrect
                                ? 'bg-danger-50 dark:bg-danger-950/50 text-danger-700 dark:text-danger-300 font-medium'
                                : isCorrect
                                  ? 'text-accent-600 dark:text-accent-300'
                                  : 'text-slate-500 dark:text-slate-400'
                          }`}
                        >
                          {isSelected ? (
                            isCorrect ? (
                              <svg
                                className="w-4 h-4 text-accent-500 dark:text-accent-300 shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4 text-danger-500 dark:text-danger-300 shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            )
                          ) : (
                            <span className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-700 shrink-0" />
                          )}
                          {a.text}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Semnare test — doar IIG/ILM, doar dacă testul e promovat ȘI cursul ARE efectiv un test
          configurat (întrebări). ILM creat fără test nu mai arată secțiunea de semnătură test. */}
      {training.passed &&
        ['IIG_SSM', 'ILM_SSM', 'IIG_SU', 'ILM_SU'].includes(training.course?.type) &&
        (training.course?.finalTest?.questions?.length || 0) > 0 &&
        (() => {
          const employeeUserId = training.employee?.userId;
          const empTestSig = testSignatures.find(
            (s) => s.signerId === employeeUserId || s.signer?.id === employeeUserId
          );
          const verifierTestSig = testSignatures.find(
            (s) => s.signerId !== employeeUserId && s.signer?.id !== employeeUserId
          );
          const isIIG = training.course?.type?.startsWith('IIG_');
          const verifierLabel = isIIG
            ? training.course?.type?.endsWith('_SSM')
              ? t('signature.verifierSSM')
              : t('signature.verifierCadruPSI')
            : t('signature.verifierManager');
          const allTestSigned = !!empTestSig && !!verifierTestSig;

          // Vizibilitate test sign: aliniat cu permisiunile backend (signTest).
          //  - IIG_SSM: rol SSM_INSTRUCTOR / SSM_COMPANY_ADMIN sau isSSMInstructor=true
          //  - IIG_SU:  isCadruTehnicPSI=true (oricare rol)
          //  - ILM_*:   CLM (department manager) — orice rol care e managerId pe departamentul angajatului
          // Plus: angajatul propriu, oricine cu acces la trainee (admins firmă, super-admin) ca să poată
          // monitoriza. Logica reală de allow e oricum verificată server-side la submit.
          const isIIGSSM = training.course?.type === 'IIG_SSM';
          const isIIGSU = training.course?.type === 'IIG_SU';
          const canSeeTestSign =
            isOwnTraining ||
            isEmployee ||
            currentUserRole === 'SUPER_ADMIN' ||
            (isIIGSSM &&
              (['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'].includes(currentUserRole) ||
                currentUser.isSSMInstructor)) ||
            (isIIGSU && currentUser.isCadruTehnicPSI) ||
            (!isIIG && currentUserRole === 'EMPLOYEE') ||
            (!isIIG && currentUserRole === 'COMPANY_ADMIN'); // admin acoperă CLM când trainee e CLM

          if (!canSeeTestSign) return null;

          return (
            <Card
              id="test-sign-section"
              className={`border-2 ${allTestSigned ? 'border-accent-200 dark:border-accent-700 bg-accent-50 dark:bg-accent-950/50' : 'border-warning-200 dark:border-warning-700 bg-warning-50 dark:bg-warning-950/50'}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-warning-600 dark:text-warning-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
                    />
                  </svg>
                  {t('signature.testSignatureLabel', { course: training.course?.title })}
                </h3>
                {training.score != null && (
                  <Badge color="green">
                    {t('signature.scoreLabel', { score: training.score })}
                  </Badge>
                )}
              </div>

              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 text-sm">
                  {empTestSig ? (
                    <svg
                      className="w-4 h-4 text-accent-500 dark:text-accent-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-warning-400 dark:border-warning-700" />
                  )}
                  <span
                    className={
                      empTestSig
                        ? 'text-accent-700 dark:text-accent-300'
                        : 'text-warning-700 dark:text-warning-300'
                    }
                  >
                    {t('signature.employee')} —{' '}
                    {empTestSig ? t('signature.statusSigned') : t('signature.statusToSign')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {verifierTestSig ? (
                    <svg
                      className="w-4 h-4 text-accent-500 dark:text-accent-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-warning-400 dark:border-warning-700" />
                  )}
                  <span
                    className={
                      verifierTestSig
                        ? 'text-accent-700 dark:text-accent-300'
                        : 'text-warning-700 dark:text-warning-300'
                    }
                  >
                    {verifierLabel} —{' '}
                    {verifierTestSig ? t('signature.statusSigned') : t('signature.statusToSign')}
                  </span>
                </div>
              </div>

              {!testSigned && (
                <Button
                  variant="accent"
                  size="lg"
                  className="w-full"
                  loading={signingTest}
                  onClick={() => {
                    setPendingTestTrainingId(trainingId);
                    setShowTestPwdModal(true);
                  }}
                >
                  {signingTest ? t('signature.signing') : t('signature.signTest')}
                </Button>
              )}
              {testSigned && (
                <div className="flex items-center gap-2 text-accent-700 dark:text-accent-300 text-sm font-medium">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t('signature.youSignedTest')}
                </div>
              )}
            </Card>
          );
        })()}

      {/* Info legală */}
      <Alert type="warning">
        <p className="text-xs">
          <strong>{t('signature.legalBasisLabel')}</strong>
          {isSU ? t('signature.legalBasisSU') : t('signature.legalBasisSSM')}
          {t('signature.legalValidityText')}
        </p>
      </Alert>

      {/* Pop-up Felicitări — apare DOAR după ce trainee-ul a semnat tot ce avea de semnat
          pe fișa asta. Înainte era pe TestPage imediat după test (cu link „Vezi certificatele")
          și userul uita să mai semneze. Acum certificatul e accesibil firesc după închiderea
          popup-ului (Card-ul cu PDF e vizibil pe pagină după ce semnezi). */}
      <Modal
        open={showCongratsModal}
        onClose={() => setShowCongratsModal(false)}
        title={t('signature.congratsTitle')}
        footer={
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowCongratsModal(false)}>
              {t('signature.stayOnPage')}
            </Button>
            {nextCourseLink && (
              <Button
                variant="accent"
                onClick={() => {
                  setShowCongratsModal(false);
                  navigate(nextCourseLink);
                }}
              >
                {nextCourseLabel || t('signature.continueNextCourse')}
              </Button>
            )}
            {!nextCourseLink && (
              <Button
                variant="accent"
                onClick={() => {
                  setShowCongratsModal(false);
                  navigate('/my-certificates');
                }}
              >
                {t('signature.viewMyCertificates')}
              </Button>
            )}
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {t('signature.congratsBody')}
          </p>
          {nextCourseLink && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('signature.moreTrainingsNote')}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SignaturePage;
